import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import "./index.css";

const isUnauthorizedError = (error: unknown): boolean => {
  if (!(error instanceof TRPCClientError)) return false;
  return error.message === UNAUTHED_ERR_MSG;
};

const isNetworkError = (error: unknown): boolean => {
  if (error instanceof TRPCClientError) {
    // 檢查是否為網路錯誤（fetch failed, timeout 等）
    const msg = error.message?.toLowerCase() || "";
    return msg.includes("fetch") || msg.includes("network") || msg.includes("timeout") || msg.includes("failed") || msg.includes("<!doctype");
  }
  return false;
};

// 追蹤 401 重定向狀態，避免重複跳轉
let isRedirectingToLogin = false;

const handleUnauthorizedRedirect = () => {
  if (isRedirectingToLogin) return;
  // 如果已經在登入頁面或首次登入頁面，不需要跳轉
  const currentPath = window.location.pathname;
  if (currentPath === "/login" || currentPath === "/first-login") return;
  // auth.me 查詢的 401 是正常行為（用於檢查登入狀態），不需要跳轉
  // 只有其他 API 的 401 才需要跳轉（表示 session 過期）
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // 401 未授權錯誤不重試
        if (isUnauthorizedError(error)) return false;
        // 網路錯誤最多重試 2 次
        if (isNetworkError(error)) return failureCount < 2;
        // 其他錯誤最多重試 1 次
        return failureCount < 1;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
    mutations: {
      retry: false, // mutation 不自動重試
    },
  },
});

const handleApiError = (error: unknown, type: "query" | "mutation") => {
  // 未登入錯誤是預期行為,不需要在控制台顯示
  if (isUnauthorizedError(error)) {
    console.log("[Auth] User not authenticated, showing login page");
    handleUnauthorizedRedirect();
    return;
  }
  // 網路錯誤使用簡潔的日誌
  if (isNetworkError(error)) {
    console.warn(`[Network] API ${type} failed - server may be unreachable`);
    return;
  }
  // 其他錯誤才顯示在控制台
  console.error(`[API ${type === "query" ? "Query" : "Mutation"} Error]`, error);
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    handleApiError(error, "query");
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    handleApiError(error, "mutation");
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
